// src/components/Backup.tsx
import { useState } from 'react';
import {
	Button,
	Group,
	Paper,
	Text,
	Stack,
	FileButton,
	Popover,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconUpload, IconInfoCircle } from '@tabler/icons-react';
import { useBackup } from '../hooks/useBackup';
import { Bill } from '../types';

interface BackupProps {
	bills: Bill[];
	onRestore: (bills: Bill[]) => void;
}

function Backup({ bills, onRestore }: BackupProps) {
	const [loading, setLoading] = useState(false);
	const { createBackupFile, restoreFromFile } = useBackup();

	const handleBackup = async () => {
		try {
			setLoading(true);
			const { link, url, filename } = await createBackupFile(bills);

			// Download file
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Cleanup URL
			setTimeout(() => URL.revokeObjectURL(url), 1000);

			notifications.show({
				title: 'Backup Created',
				message: `Your bills have been backed up to ${filename}`,
				color: 'green',
			});
		} catch (error) {
			notifications.show({
				title: 'Backup Failed',
				message: 'Failed to create backup file. Please try again.',
				color: 'red',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleRestore = async (file: File | null) => {
		if (!file) return;
		try {
			setLoading(true);
			const backupData = await restoreFromFile(file);
			onRestore(backupData.bills);

			notifications.show({
				title: 'Restore Successful',
				message: `Restored ${backupData.stats.totalBills} bills from ${backupData.date} ${backupData.time}`,
				color: 'green',
			});
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Unknown error';
			notifications.show({
				title: 'Restore Failed',
				message: errorMessage,
				color: 'red',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Paper withBorder p='md' radius='md'>
			<Stack gap='md'>
				<Group justify='apart' align='center'>
					<Text size='sm' fw={500}>
						Local Backup
					</Text>
					<Popover
						width={300}
						position='bottom'
						withArrow
						shadow='md'
						transitionProps={{
							duration: 285,
						}}
					>
						<Popover.Target>
							<IconInfoCircle
								size={18}
								style={{ cursor: 'pointer' }}
							/>
						</Popover.Target>
						<Popover.Dropdown>
							<Text size='sm'>
								Your data is saved in a downloadable backup file
								stored in your phone's Downloads folder, so it
								won't be removed if you clear your browser data.
								However, if you don't keep this backup file,
								your data may be lost. For improved security and
								easier recovery, we recommend using Google Cloud
								backup with Google sign-in.
							</Text>
						</Popover.Dropdown>
					</Popover>
					<Text size='sm' c='dimmed'>
						Create a backup of your bills or restore from a previous
						backup file.
					</Text>
				</Group>
				<Group>
					<Button
						leftSection={<IconDownload size={16} />}
						onClick={handleBackup}
						loading={loading}
					>
						Create Backup
					</Button>

					<FileButton
						onChange={handleRestore}
						accept='application/json'
					>
						{(props) => (
							<Button
								variant='light'
								leftSection={<IconUpload size={16} />}
								loading={loading}
								{...props}
							>
								Restore
							</Button>
						)}
					</FileButton>
				</Group>
			</Stack>
		</Paper>
	);
}

export default Backup;
